import React, { useState } from 'react'
import { Alert, Button, Icon, Modal, Spin } from 'antd'
import NewHeader from '../../../../components/NewHeader'
import BrandChoicePanel from './components/BrandChoicePanel'
import BrandChat from './components/BrandChat'
import BrandProfilePanel from './components/BrandProfilePanel'
import { useBrandOnboarding } from './hooks'
import './BrandOnboarding.scss'

const BrandOnboarding = ({
  organizationId,
  initialSessionId,
  shouldCreateSession,
  onComplete,
  onSessionChange,
  onGoToVideoList
}) => {
  const [isNewSessionModalVisible, setIsNewSessionModalVisible] = useState(
    false
  )

  const onboarding = useBrandOnboarding({
    organizationId,
    initialSessionId,
    shouldCreateSession,
    onComplete,
    onSessionChange
  })

  const {
    sessionId,
    sessionStatus,
    viewMode,
    messages,
    uploadedFiles,
    metadata,
    brandProfile,
    brandDocumentUrl,
    brandImageUrl,
    isCreatingSession,
    isResumedSession,
    isStreaming,
    isFinalizing,
    isDownloadingBrandDocument,
    error,
    canFinalize,
    acceptedExtensions,
    startFromScratch,
    uploadGuidelines,
    sendMessage,
    returnToSetupOptions,
    editBrandProfile,
    viewBrandProfile,
    downloadBrandDocument,
    finalizeProfile,
    continueToVideo,
    createNewSession,
    retryCreateSession
  } = onboarding

  const handleOpenNewSessionModal = () => {
    if (isCreatingSession || isStreaming || isFinalizing) return
    setIsNewSessionModalVisible(true)
  }

  const handleCloseNewSessionModal = () => {
    if (isCreatingSession) return
    setIsNewSessionModalVisible(false)
  }

  const handleCreateNewSession = async () => {
    const didCreateSession = await createNewSession()

    if (didCreateSession) {
      setIsNewSessionModalVisible(false)
    }
  }

  const renderHeader = ({ onBack } = {}) => (
    <header className="brand-onboarding__header">
      <div className="brand-onboarding__header-main">
        {onBack ? (
          <Button
            className="brand-onboarding__header-back"
            onClick={onBack}
            title="Back to setup"
          >
            <Icon type="left" />
          </Button>
        ) : null}
        <div className="brand-onboarding__mark">
          <Icon type="skin" />
        </div>
        <div>
          <div className="brand-onboarding__kicker">
            Organization brand setup
          </div>
          <h1>AI Video Brand Onboarding</h1>
          <p>
            Build the brand profile that guides video style, voice, imagery,
            music, and defaults.
          </p>
        </div>
      </div>
      <div className="brand-onboarding__header-right">
        <NewHeader />
      </div>
    </header>
  )

  const renderBody = () => {
    if (isCreatingSession) {
      return (
        <div className="brand-onboarding__loading">
          <Spin size="large" />
          <div>
            <h2>Preparing brand onboarding</h2>
            <p>Starting a guided setup session for this organization.</p>
          </div>
        </div>
      )
    }

    if (!sessionId && error) {
      return (
        <div className="brand-onboarding__empty">
          <Icon type="warning" />
          <h2>Brand onboarding could not start</h2>
          <p>{error}</p>
          <div className="brand-onboarding__empty-actions">
            <Button type="primary" onClick={retryCreateSession}>
              Try again
            </Button>
            <Button icon="unordered-list" onClick={onGoToVideoList}>
              Go to video list
            </Button>
          </div>
        </div>
      )
    }

    if (viewMode === 'summary') {
      return (
        <BrandProfilePanel
          brandProfile={brandProfile || {}}
          brandImageUrl={brandImageUrl}
          brandDocumentUrl={brandDocumentUrl}
          headerContent={renderHeader({ onBack: returnToSetupOptions })}
          onEditProfile={editBrandProfile}
          onDownloadDocument={downloadBrandDocument}
          isDownloadingDocument={isDownloadingBrandDocument}
          onContinue={continueToVideo}
        />
      )
    }

    if (viewMode === 'chat') {
      return (
        <BrandChat
          messages={messages}
          metadata={metadata}
          uploadedFiles={uploadedFiles}
          isStreaming={isStreaming}
          isFinalizing={isFinalizing}
          isProfileCompleted={sessionStatus === 'completed'}
          canFinalize={canFinalize}
          acceptedExtensions={acceptedExtensions}
          onBackToSetup={returnToSetupOptions}
          onUploadGuidelines={uploadGuidelines}
          onSendMessage={sendMessage}
          onFinalize={finalizeProfile}
        />
      )
    }

    return (
      <BrandChoicePanel
        disabled={isStreaming || isFinalizing}
        isResumedSession={isResumedSession}
        acceptedExtensions={acceptedExtensions}
        hasCompletedProfile={Boolean(brandProfile)}
        onUploadGuidelines={uploadGuidelines}
        onStartFromScratch={startFromScratch}
        onViewProfile={viewBrandProfile}
        onGoToVideoList={onGoToVideoList || continueToVideo}
      />
    )
  }

  const showActiveSessionNotice =
    Boolean(sessionId) && !isCreatingSession && viewMode !== 'summary'

  return (
    <div
      className={`brand-onboarding${
        viewMode === 'summary' ? ' brand-onboarding--summary' : ''
      }`}
    >
      {viewMode === 'summary' ? null : renderHeader()}

      {error && sessionId ? (
        <Alert
          className="brand-onboarding__alert"
          type="error"
          showIcon
          message={error}
        />
      ) : null}

      {showActiveSessionNotice ? (
        <section className="brand-onboarding__session-notice" role="status">
          <div className="brand-onboarding__session-notice-icon">
            <Icon type="safety-certificate" />
          </div>
          <div className="brand-onboarding__session-notice-copy">
            <strong>
              {isResumedSession
                ? 'Continuing your existing brand discovery'
                : 'Your new brand discovery is ready'}
            </strong>
            <p>
              {isResumedSession
                ? 'Uploads, answers, and edits will continue in this discovery.'
                : 'Choose how you would like to begin. Your work will be saved automatically.'}{' '}
              <span>
                Discovery ID: <code title={sessionId}>{sessionId}</code>
              </span>
            </p>
          </div>
          <Button
            className="brand-onboarding__new-session-button"
            disabled={isStreaming || isFinalizing}
            onClick={handleOpenNewSessionModal}
          >
            <Icon type="plus-circle" />
            Start a new discovery
          </Button>
        </section>
      ) : null}

      <main className="brand-onboarding__body">{renderBody()}</main>

      <Modal
        visible={isNewSessionModalVisible}
        closable={!isCreatingSession}
        maskClosable={!isCreatingSession}
        footer={null}
        width={460}
        className="brand-onboarding__new-session-modal"
        onCancel={handleCloseNewSessionModal}
      >
        <div className="brand-onboarding__new-session-confirm">
          <div className="brand-onboarding__new-session-icon">
            <Icon type="warning" />
          </div>
          <h2>Start a new brand discovery?</h2>
          <p>
            This will open a fresh setup and replace the discovery currently
            shown here. Its chat, uploads, progress, and profile will no longer
            appear in this workspace.
          </p>
          <div className="brand-onboarding__replaced-session">
            <span>Discovery being replaced</span>
            <code title={sessionId}>{sessionId}</code>
          </div>
          <div className="brand-onboarding__new-session-actions">
            <Button
              disabled={isCreatingSession}
              onClick={handleCloseNewSessionModal}
            >
              Keep current discovery
            </Button>
            <Button
              type="danger"
              loading={isCreatingSession}
              onClick={handleCreateNewSession}
            >
              Start new discovery
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default BrandOnboarding
